import { useAuthStore } from '../stores/authStore'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'

interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: {
    code: string
    message: string
  }
}

class ApiClient {
  private baseUrl: string

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  private getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    // Get current auth state from Zustand store
    const state = useAuthStore.getState()
    
    if (state.token) {
      headers['Authorization'] = `Bearer ${state.token}`
    }

    // Always send tenant ID (backend uses demo tenant if not authenticated)
    if (state.activeTenant?.tenantId) {
      headers['X-Tenant-ID'] = state.activeTenant.tenantId
    }

    return headers
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown
  ): Promise<ApiResponse<T>> {
    const headers = this.getAuthHeaders()

    const response = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    })

    return response.json()
  }

  // Auth
  async register(email: string, password: string, businessName: string, industry: string) {
    return this.request<{ tenantId: string; userId: string }>('POST', '/v1/auth/register', {
      email,
      password,
      businessName,
      industry,
    })
  }

  // Dashboard
  async getDashboard() {
    return this.request<{
      kpis: { revenue: number; expenses: number; netIncome: number; cashBalance: number }
      reconciliation: { matchedPercent: number; unmatchedCount: number }
      recentTransactions: unknown[]
    }>('GET', '/v1/dashboard')
  }

  // Transactions
  async getTransactions(params?: { status?: string; startDate?: string; endDate?: string }) {
    const query = new URLSearchParams(params as Record<string, string>).toString()
    return this.request<{ items: unknown[]; count: number }>('GET', `/v1/transactions?${query}`)
  }

  async updateTransaction(id: string, data: { categoryId: string; categoryName: string }) {
    return this.request('PUT', `/v1/transactions/${id}`, data)
  }

  async confirmTransaction(id: string) {
    return this.request('POST', `/v1/transactions/${id}/confirm`)
  }

  // Reconciliation
  async getReconciliationStatus() {
    return this.request<{
      totalTransactions: number
      statusCounts: Record<string, number>
      matchedPercent: number
      unmatchedCount: number
    }>('GET', '/v1/reconciliation/status')
  }

  async runReconciliation() {
    return this.request('POST', '/v1/reconciliation/run')
  }

  async getUnmatchedItems() {
    return this.request<{ items: unknown[]; count: number }>('GET', '/v1/reconciliation/unmatched')
  }

  // Reports
  async getProfitLoss(startDate: string, endDate: string) {
    return this.request<{
      revenue: number
      cogs: number
      grossProfit: number
      expenses: number
      netIncome: number
    }>('GET', `/v1/reports/pnl?startDate=${startDate}&endDate=${endDate}`)
  }

  async getBalanceSheet(asOfDate: string) {
    return this.request<{
      assets: unknown
      liabilities: unknown
      equity: unknown
    }>('GET', `/v1/reports/balance-sheet?asOfDate=${asOfDate}`)
  }

  // Seed/Demo
  async seedRestaurant(locationId: string, days: number) {
    return this.request<{
      transactionsCreated: number
      posTransactionsCreated: number
      posBatchesCreated: number
    }>('POST', `/v1/seed/restaurant?locationId=${locationId}`, { days })
  }

  async seedRetail(locationId: string, days: number) {
    return this.request('POST', `/v1/seed/retail?locationId=${locationId}`, { days })
  }

  // Categories
  async getCategories() {
    return this.request<{ items: unknown[]; count: number }>('GET', '/v1/categories')
  }

  // Accounts
  async getAccounts() {
    return this.request<{ items: unknown[]; count: number }>('GET', '/v1/accounts')
  }
}

export const api = new ApiClient(API_URL)
